import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { shopAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ShoppingCart, Check, Package, Loader2, ChevronRight, ArrowLeft,
  CreditCard, Building2, Truck, AlertCircle, CheckCircle2, Gift
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const formatPrice = (price) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

const ShopPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [showOrders, setShowOrders] = useState(false);

  const [shipping, setShipping] = useState({
    name: user?.name || '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
    phone: user?.phone || '',
    notes: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('vorkasse');
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await shopAPI.getProducts();
        setProducts(res.data.products || []);
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (searchParams.get('paypal_success') === 'true') {
      setOrderSuccess({ payment_method: 'paypal', message: 'PayPal-Zahlung erfolgreich! Ihre Bestellung wird bearbeitet.' });
    }
  }, [searchParams]);

  const fetchOrders = async () => {
    try {
      const res = await shopAPI.getOrders();
      setOrders(res.data || []);
      setShowOrders(true);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  };

  const handleOrder = async () => {
    if (!shipping.name || !shipping.street || !shipping.zip || !shipping.city) {
      setError('Bitte füllen Sie alle Pflichtfelder der Lieferadresse aus.');
      return;
    }
    setOrdering(true);
    setError('');
    try {
      const res = await shopAPI.createOrder({
        product_id: selectedProduct.id,
        payment_method: paymentMethod,
        shipping_name: shipping.name,
        shipping_street: shipping.street,
        shipping_zip: shipping.zip,
        shipping_city: shipping.city,
        shipping_country: shipping.country,
        phone: shipping.phone,
        notes: shipping.notes,
      });

      if (paymentMethod === 'paypal' && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }

      setOrderSuccess(res.data);
      setShowCheckout(false);
      setSelectedProduct(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Bestellung fehlgeschlagen.');
    } finally {
      setOrdering(false);
    }
  };

  // Order success state
  if (orderSuccess) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto" data-testid="order-success">
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Bestellung aufgegeben!
            </h1>
            {orderSuccess.payment_method === 'vorkasse' ? (
              <>
                <p className="text-slate-400 mb-6">
                  Ihre Bestellung <span className="text-[#EC2229] font-mono">{orderSuccess.order_number}</span> wurde erfolgreich aufgegeben.
                </p>
                <div className="glass-card p-6 mb-6 text-left space-y-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Zahlungsart</p>
                    <p className="text-white font-medium">Vorkasse (Banküberweisung)</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Gesamtbetrag (Brutto)</p>
                    <p className="text-xl font-mono font-bold text-[#EC2229]">{formatPrice(orderSuccess.price_gross)}</p>
                  </div>
                  <div className="pt-3 border-t border-white/10 space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Bankverbindung</p>
                    <div className="bg-white/5 rounded-lg p-4 space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Kontoinhaber</span>
                        <span className="text-white font-medium">TrXL Performance – Sven Trachsel</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">IBAN</span>
                        <span className="text-white font-mono font-medium">DE68 2915 1700 1011 7797 98</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Bank</span>
                        <span className="text-white">Kreissparkasse Syke</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Verwendungszweck</span>
                        <span className="font-mono text-[#EC2229] font-medium">{orderSuccess.order_number}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400">
                    Sie erhalten eine E-Mail mit den Überweisungsdaten. Wir versenden nach Zahlungseingang.
                  </p>
                </div>
              </>
            ) : (
              <p className="text-slate-400 mb-6">{orderSuccess.message || 'Ihre Bestellung wird bearbeitet.'}</p>
            )}
            <Button onClick={() => { setOrderSuccess(null); }} className="btn-primary" data-testid="back-to-shop-btn">
              Zurück zum Shop
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Orders list
  if (showOrders) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto" data-testid="shop-orders">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Meine Bestellungen</h1>
              <p className="text-slate-400 mt-1 text-sm">Übersicht Ihrer Shop-Bestellungen</p>
            </div>
            <Button variant="outline" onClick={() => setShowOrders(false)} className="btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zum Shop
            </Button>
          </div>
          {orders.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-white font-medium">Noch keine Bestellungen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div key={order.id} className="glass-card p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm text-[#EC2229]">{order.order_number}</p>
                      <p className="text-white font-medium mt-1">{order.product_name}</p>
                      <p className="text-xs text-slate-500 mt-1">{new Date(order.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-white">{formatPrice(order.price_gross)}</p>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold mt-1 ${
                        order.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                        order.status === 'awaiting_payment' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                        'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      }`}>
                        {order.status === 'awaiting_payment' ? 'Zahlung ausstehend' : order.status === 'completed' ? 'Abgeschlossen' : order.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Checkout
  if (showCheckout && selectedProduct) {
    const vatAmount = Math.round(selectedProduct.price_net * selectedProduct.vat_rate) / 100;
    const grossPrice = selectedProduct.price_net + vatAmount;

    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto" data-testid="shop-checkout">
          <button onClick={() => setShowCheckout(false)} className="text-sm text-slate-400 hover:text-white mb-6 flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Zurück zum Produkt
          </button>

          <h1 className="font-heading text-2xl font-bold text-white mb-6">Bestellung abschließen</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping */}
              <div className="glass-card p-6">
                <h2 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#EC2229]" /> Lieferadresse
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label className="label-text">Name / Firma *</Label>
                    <Input value={shipping.name} onChange={e => setShipping(p => ({...p, name: e.target.value}))} className="input-field" data-testid="shipping-name" />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label className="label-text">Straße + Hausnr. *</Label>
                    <Input value={shipping.street} onChange={e => setShipping(p => ({...p, street: e.target.value}))} className="input-field" data-testid="shipping-street" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-text">PLZ *</Label>
                    <Input value={shipping.zip} onChange={e => setShipping(p => ({...p, zip: e.target.value}))} className="input-field" data-testid="shipping-zip" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-text">Stadt *</Label>
                    <Input value={shipping.city} onChange={e => setShipping(p => ({...p, city: e.target.value}))} className="input-field" data-testid="shipping-city" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-text">Telefon</Label>
                    <Input value={shipping.phone} onChange={e => setShipping(p => ({...p, phone: e.target.value}))} className="input-field" data-testid="shipping-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-text">Land</Label>
                    <Input value={shipping.country} onChange={e => setShipping(p => ({...p, country: e.target.value}))} className="input-field" data-testid="shipping-country" />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label className="label-text">Anmerkungen</Label>
                    <Textarea value={shipping.notes} onChange={e => setShipping(p => ({...p, notes: e.target.value}))} className="input-field min-h-[60px]" data-testid="shipping-notes" />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="glass-card p-6">
                <h2 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#EC2229]" /> Zahlungsart
                </h2>
                <div className="space-y-3">
                  <button
                    onClick={() => setPaymentMethod('vorkasse')}
                    className={`w-full p-4 rounded-lg border text-left transition-all flex items-center gap-4 ${paymentMethod === 'vorkasse' ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-1 ring-[#EC2229]/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                    data-testid="payment-vorkasse"
                  >
                    <Building2 className={`w-6 h-6 ${paymentMethod === 'vorkasse' ? 'text-[#EC2229]' : 'text-slate-500'}`} />
                    <div>
                      <p className="text-white font-medium">Vorkasse (Banküberweisung)</p>
                      <p className="text-xs text-slate-400">Versand nach Zahlungseingang</p>
                    </div>
                    {paymentMethod === 'vorkasse' && <Check className="w-5 h-5 text-[#EC2229] ml-auto" />}
                  </button>
                  <button
                    onClick={() => setPaymentMethod('paypal')}
                    className={`w-full p-4 rounded-lg border text-left transition-all flex items-center gap-4 ${paymentMethod === 'paypal' ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-1 ring-[#EC2229]/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                    data-testid="payment-paypal"
                  >
                    <svg className={`w-6 h-6 ${paymentMethod === 'paypal' ? 'text-[#EC2229]' : 'text-slate-500'}`} viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.566c-.574 0-1.06.418-1.15.987l-.758 4.812-.002.015-.39 2.468a.477.477 0 0 1-.47.402h-.72z"/></svg>
                    <div>
                      <p className="text-white font-medium">PayPal</p>
                      <p className="text-xs text-slate-400">Sofortige Zahlung über PayPal</p>
                    </div>
                    {paymentMethod === 'paypal' && <Check className="w-5 h-5 text-[#EC2229] ml-auto" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div>
              <div className="glass-card p-6 sticky top-20">
                <h2 className="font-heading font-semibold text-white mb-4">Zusammenfassung</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <img src={selectedProduct.image_url} alt={selectedProduct.name} className="w-16 h-12 object-cover rounded" />
                    <div>
                      <p className="text-white text-sm font-medium">{selectedProduct.name}</p>
                      <p className="text-xs text-slate-500">{selectedProduct.brand}</p>
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Netto</span>
                      <span className="text-white font-mono">{formatPrice(selectedProduct.price_net)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">MwSt. ({selectedProduct.vat_rate}%)</span>
                      <span className="text-white font-mono">{formatPrice(vatAmount)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t border-white/10 pt-2">
                      <span className="text-white">Gesamt (Brutto)</span>
                      <span className="text-[#EC2229] font-mono text-lg">{formatPrice(grossPrice)}</span>
                    </div>
                  </div>
                </div>
                <Button onClick={handleOrder} disabled={ordering} className="btn-primary w-full" data-testid="place-order-btn">
                  {ordering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                  {paymentMethod === 'paypal' ? 'Mit PayPal bezahlen' : 'Bestellung aufgeben'}
                </Button>
                <p className="text-[10px] text-slate-500 text-center mt-3">Alle Preise zzgl. Versand. Es gelten unsere AGB.</p>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Product Detail
  if (selectedProduct) {
    const vatAmount = Math.round(selectedProduct.price_net * selectedProduct.vat_rate) / 100;
    const grossPrice = selectedProduct.price_net + vatAmount;
    const images = selectedProduct.images || [selectedProduct.image_url];

    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto" data-testid="shop-product-detail">
          <button onClick={() => setSelectedProduct(null)} className="text-sm text-slate-400 hover:text-white mb-6 flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div className="space-y-3">
              <div className="glass-card p-2 rounded-xl overflow-hidden">
                <img src={images[activeImg]} alt={selectedProduct.name} className="w-full h-80 object-contain rounded-lg bg-white/5" />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? 'border-[#EC2229]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <span className="badge-neutral text-xs mb-2 inline-block">{selectedProduct.brand}</span>
                <h1 className="font-heading text-2xl font-bold text-white">{selectedProduct.name}</h1>
                <p className="text-slate-400 mt-2 text-sm leading-relaxed">{selectedProduct.description}</p>
              </div>

              {/* Bonus Banner */}
              {selectedProduct.bonus && (
                <div className="p-4 rounded-xl bg-[#EC2229]/10 border border-[#EC2229]/30">
                  <p className="text-[#EC2229] font-semibold flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    {selectedProduct.bonus}
                  </p>
                </div>
              )}

              <div className="glass-card p-4">
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-heading font-bold text-white">{formatPrice(selectedProduct.price_net)}</span>
                  <span className="text-sm text-slate-500 mb-1">netto</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Brutto: {formatPrice(grossPrice)} (inkl. {selectedProduct.vat_rate}% MwSt.)
                </p>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setShowCheckout(true)} className="btn-primary flex-1" data-testid="buy-now-btn">
                  <ShoppingCart className="w-4 h-4 mr-2" /> Jetzt bestellen
                </Button>
              </div>

              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Zahlungsarten</p>
                <div className="flex gap-2">
                  <span className="badge-neutral text-xs">Vorkasse</span>
                  <span className="badge-neutral text-xs">PayPal</span>
                </div>
              </div>
            </div>
          </div>

          {/* Features & Includes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div className="glass-card p-6">
              <h2 className="font-heading font-semibold text-white mb-4">Funktionen & Features</h2>
              <ul className="space-y-2">
                {selectedProduct.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card p-6">
              <h2 className="font-heading font-semibold text-white mb-4">Im Lieferumfang enthalten</h2>
              <ul className="space-y-2">
                {selectedProduct.includes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Package className="w-4 h-4 text-[#EC2229] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Specs */}
          {selectedProduct.specs && (
            <div className="glass-card p-6 mt-6">
              <h2 className="font-heading font-semibold text-white mb-4">Technische Spezifikationen</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(selectedProduct.specs).filter(([k]) => typeof selectedProduct.specs[k] === 'object').map(([key, spec]) => (
                  <div key={key}>
                    <h3 className="text-sm font-semibold text-[#EC2229] mb-2 capitalize">{key === 'obd2' ? 'OBD2 Sonde' : key === 'dynopro' ? 'DynoPRO Sonde' : key}</h3>
                    {Object.entries(spec).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs py-1 border-b border-white/5">
                        <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-slate-300">{v}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {selectedProduct.specs.total_weight && (
                <p className="text-xs text-slate-500 mt-4">Gesamtgewicht des Sets: {selectedProduct.specs.total_weight}</p>
              )}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Products List
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="shop-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Shop</h1>
            <p className="text-slate-400 mt-1 text-sm">Professionelle Chiptuning-Tools und Messgeräte</p>
          </div>
          <Button variant="outline" onClick={fetchOrders} className="btn-secondary" data-testid="my-orders-btn">
            <Package className="w-4 h-4 mr-2" /> Meine Bestellungen
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {products.map(product => {
              const vatAmount = Math.round(product.price_net * product.vat_rate) / 100;
              const grossPrice = product.price_net + vatAmount;
              return (
                <div
                  key={product.id}
                  className="glass-card overflow-hidden group hover:border-[#EC2229]/20 transition-all cursor-pointer"
                  onClick={() => { setSelectedProduct(product); setActiveImg(0); }}
                  data-testid={`product-${product.id}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="badge-neutral text-xs">{product.brand}</span>
                        <h3 className="font-heading text-lg font-semibold text-white mt-2 group-hover:text-[#EC2229] transition-colors">{product.name}</h3>
                        <p className="text-sm text-slate-400 mt-1">{product.short_desc}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-2xl font-heading font-bold text-white">{formatPrice(product.price_net)}</p>
                        <p className="text-[10px] text-slate-500 uppercase">netto</p>
                      </div>
                    </div>
                    {product.bonus && (
                      <div className="mt-3 p-2.5 rounded-lg bg-[#EC2229]/10 border border-[#EC2229]/20">
                        <p className="text-xs text-[#EC2229] font-semibold flex items-center gap-1.5">
                          <Gift className="w-3.5 h-3.5" /> {product.bonus}
                        </p>
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex gap-2">
                        <span className="badge-neutral text-xs">Vorkasse</span>
                        <span className="badge-neutral text-xs">PayPal</span>
                      </div>
                      <span className="text-sm text-[#EC2229] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Details <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ShopPage;
